import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Activity, CheckCircle, XCircle, AlertCircle, Clock } from "lucide-react";

export default function SystemStatus() {
  const services = [
    {
      name: "Authentication Service",
      status: "operational",
      description: "User login and authentication"
    },
    {
      name: "Contribution System",
      status: "operational",
      description: "Contribution recording and management"
    },
    {
      name: "Loan Management",
      status: "operational",
      description: "Loan applications and payments"
    },
    {
      name: "Reporting System",
      status: "operational",
      description: "Financial reports and analytics"
    },
    {
      name: "Notification Service",
      status: "degraded",
      description: "Email and SMS notifications"
    },
    {
      name: "File Upload Service",
      status: "operational",
      description: "Document and image uploads"
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "operational":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "degraded":
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case "outage":
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "operational":
        return <Badge className="bg-green-100 text-green-800">Operational</Badge>;
      case "degraded":
        return <Badge className="bg-yellow-100 text-yellow-800">Degraded</Badge>;
      case "outage":
        return <Badge className="bg-red-100 text-red-800">Outage</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight" style={{ color: '#050500' }}>
            System Status
          </h1>
          <p style={{ color: '#050500', opacity: 0.7 }}>
            Current status of all system services and components
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Activity className="h-5 w-5 text-green-500" />
          <span className="text-sm font-medium text-green-600">All Systems Operational</span>
        </div>
      </div>

      {/* Overall Status */}
      <Card>
        <CardHeader>
          <CardTitle>System Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">5</div>
              <p className="text-sm text-gray-500">Operational</p>
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-600">1</div>
              <p className="text-sm text-gray-500">Degraded</p>
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600">0</div>
              <p className="text-sm text-gray-500">Outages</p>
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-600">99.9%</div>
              <p className="text-sm text-gray-500">Uptime</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Service Status */}
      <Card>
        <CardHeader>
          <CardTitle>Service Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {services.map((service, index) => (
              <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center space-x-4">
                  {getStatusIcon(service.status)}
                  <div>
                    <p className="font-medium">{service.name}</p>
                    <p className="text-sm text-gray-500">{service.description}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {getStatusBadge(service.status)}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Incidents */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Incidents</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start space-x-4 p-4 border rounded-lg">
              <AlertCircle className="h-5 w-5 text-yellow-500 mt-1" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <p className="font-medium">Notification Service Degraded</p>
                  <span className="text-sm text-gray-500">2 hours ago</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Some email notifications may be delayed. SMS notifications are working normally.
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4 p-4 border rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-500 mt-1" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <p className="font-medium">Scheduled Maintenance Completed</p>
                  <span className="text-sm text-gray-500">1 day ago</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Database optimization completed successfully. All services are running normally.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Maintenance Schedule */}
      <Card>
        <CardHeader>
          <CardTitle>Upcoming Maintenance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center space-x-4">
                <Clock className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="font-medium">Monthly System Update</p>
                  <p className="text-sm text-gray-500">Security patches and performance improvements</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-medium">Dec 15, 2024</p>
                <p className="text-sm text-gray-500">2:00 AM - 4:00 AM</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
